import React, { useCallback, useEffect, useMemo } from "react";

import cx from 'classnames';

import Slider from "src/components/slider";
import Button from "src/components/button";
import { Asset, AssetFile, AssetUpload } from "src/types/assets";

import styles from "./add.module.scss";
import { AddAssetsFromHub } from "./hub";
import { animated, Spring, useTransition } from "@react-spring/web";
import _ from "lodash";
import { Collection } from "src/types/collections";
import { AssetCard, AssetUploadCard, CleverstoryAssetCard, LinkAssetCard, PodcastCard, SimulationCard } from "./card";
import AddUrlAsset from "./components/links";
import AddAssetFromCleverStory from "./components/cleverstory";

import Dropzone, { DropzoneRef } from "react-dropzone";
import { nanoid } from "nanoid";
import { FileService } from "src/utils/file.service";
import S3Upload from "react-s3-uploader/s3upload";
import AuthService from "src/utils/auth.service";
import FileUploadService from "src/utils/file.upload.service";
import { useDispatch } from "react-redux";
import { createAssetFromFile, invalidateAssetUpload, invalidateLinkAsset, toggleAssetStatusInContentSimilarityQueue, uploadAsset, uploadAssetFailed, uploadAssetProgress, uploadingAssetSucceeded } from "src/redux/actions/assets";
import { useSelector } from "react-redux";
import { fetchActivatedWorkSpaces, invalidateCleverstoryAssets } from "src/redux/actions/cleverstory";
import { useAssetAdoption } from "src/redux/actions/adoption/asset";
import { Group } from "src/types/groups";
import { Stream } from "src/types/streams";
import { Course } from "src/types/coaching/courses";
import { Podcast } from "src/types/coaching/podcast";
import DealRoom from "src/pages/dealRoom";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_ASSETS_API_URL}/assets/3.0`

interface AssetState {
  option: 'hub-assets' | 'cleverstory-assets' | null;
  entities: (Asset | Collection | any)[];
}

interface SourceProps {
  type: 'collection' | 'stream' | 'group' | 'seek' | 'compose' | 'course' | 'podcast' | 'dealRoom';
  entity: Collection | Stream | Group | Course | Podcast | null;
  isOpenedFromCollection?: boolean;
}

interface AddAssetsDialogProps {
  show: boolean;
  selectableEntityTypes: string[];
  source?: SourceProps;
  allowInternalEntities?: boolean;
  allowBackgroundUpload?: boolean;
  requireOptimizedAssets?: boolean;
  initialEntities?: (Asset | Collection)[];
  // eslint-disable-next-line no-unused-vars
  selectionFilter?: (entity: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;
  // eslint-disable-next-line no-unused-vars
  onFinish: (entities: (Asset | AssetUpload | Collection)[]) => any;

  onClose: () => void;
  onBack?: () => void;
  onSelectionChange?: (entities: any[]) => void;
  className? : string; 
}

const AddAssetsDialog: React.FC<AddAssetsDialogProps> = ({ 
  show, selectableEntityTypes, source, 
  selectionFilter, entityAction, 
  onFinish, onClose, 
  allowBackgroundUpload=true,
  requireOptimizedAssets=false,
  allowInternalEntities=false,
  initialEntities=[],
  onBack,
  onSelectionChange,
  className
}) => {
  const dispatch = useDispatch();

  const { trackAddAssetScreenView, trackAssetAdded } = useAssetAdoption();

  const [state, setState] = React.useState<AssetState>({ option: null, entities: initialEntities });

  const hasEntities = state.entities?.length > 0;

  const assetUploads = useSelector(
    (reduxState) => _.get(reduxState, 'asset.upload', {}) as Record<string, any>,
    _.isEqual,
  );

  const getPersonalDriveUpload = useCallback((entity): any => {
    return _.find(assetUploads, (upload: any) => {
      return upload?.created && (
        _.get(upload, 'item.id') === entity.id ||
        upload?.id === entity.id
      );
    });
  }, [assetUploads]);

  const isOptimised = useCallback((entity) => {
    if (entity.entityType === 'asset-upload') return false;

    if (entity.entityType === 'asset') {
      const upload = getPersonalDriveUpload(entity);
      if (upload) {
      const status = (_.get(upload, 'item.status') || entity.status || '') as string;
      return status.toLowerCase() === 'ok';
      }
    }

    return true;
  }, [getPersonalDriveUpload]);

  const canFinish = useMemo(() => {
    if (allowBackgroundUpload && _.size(state.entities) > 0) {
      return true;
    }

    const entities = state.entities || [];
    if (_.size(entities) === 0) return false;

    if (requireOptimizedAssets) {
      if (selectionFilter && entities.some(entity => !selectionFilter({
        ...entity,
        entityType: entity.entityType || 'asset',
        metadata: entity.metadata || getPersonalDriveUpload(entity)?.item?.metadata,
      }))) return false;
      return entities.every(isOptimised);
    }

    return _.size(entities.filter(isOptimised)) > 0;
  }, [state.entities, allowBackgroundUpload, requireOptimizedAssets, selectionFilter, isOptimised, getPersonalDriveUpload]);

  const newAssetsCreated = useSelector((reduxState) => {
    const uploadState = _.get(reduxState, 'asset.upload');

    const items = _.keys(uploadState).filter(key => {
      const upload = _.get(uploadState, `[${key}]`);
      return _.get(upload, 'created');
    }).map(key => {
      return _.get(uploadState, `[${key}]`);
    });

    return items;
  }, _.isEqual);

  const failedAssets = state.entities.filter(entity => entity != null && _.size(_.get(entity, "error.errors"))> 0);

  const newLinksCreated = useSelector((reduxState) => {
    const linksState = _.get(reduxState, 'asset.linkAsset');

    const items = _.keys(linksState).filter(key => {
      const upload = _.get(linksState, `[${key}]`);
      return _.get(upload, 'created');
    }).map(key => {
      return _.get(linksState, `[${key}]`);
    });

    return items;
  }, _.isEqual);

  const failedLinks = useSelector((reduxState) => {
    const linksState = _.get(reduxState, 'asset.linkAsset');

    const errorLinks = _.keys(linksState).filter(key => {
      const upload = _.get(linksState, `[${key}]`);
      return _.get(upload, 'error');
    }).map(key => {
      return _.get(linksState, `[${key}]`);
    });

    return errorLinks;
  }, _.isEqual);

  const newPagesCreated = useSelector((reduxState) => {
    const pagesState = _.get(reduxState, 'cleverStory.create');

    const items = _.keys(pagesState).filter(key => {
      const upload = _.get(pagesState, `[${key}]`);
      return _.get(upload, 'created');
    }).map(key => {
      return _.get(pagesState, `[${key}]`);
    });

    return items;
  }, _.isEqual);

  useEffect(() => {
    if (show) {
      trackAddAssetScreenView();
      dispatch(fetchActivatedWorkSpaces())
    }

    setState({
      option: null,
      entities: initialEntities || []
    });

  }, [show]);

  const handleStateChange = (state: AssetState) => {
    setState(state);
  }

  const handleClose = () => {

    invalidateNewEntities();
    (state.entities || []).forEach((entity) => {
      if (entity.entityType === 'asset-upload') {
        const uploader = FileUploadService.getUploader(entity.id);

        if (uploader) {
          console.log(`Aborting upload ${entity.id}`);
          uploader.abortUpload();
        }
        dispatch(invalidateAssetUpload(entity.id));
      }
    });

    onClose();
  }

  const invalidateNewEntities = () => {
    if (_.size(newAssetsCreated) > 0) {
      newAssetsCreated.forEach((asset: any) => {
        dispatch(invalidateAssetUpload(asset.id));
      });
    }

    if (_.size(newLinksCreated) > 0) {
      newLinksCreated.forEach((link: any) => {
        dispatch(invalidateLinkAsset(link.id));
      });
    }

    if (_.size(newPagesCreated) > 0) {
      newPagesCreated.forEach((page: any) => {
        dispatch(invalidateCleverstoryAssets(page.id));
      });
    }

    if (_.size(failedAssets) > 0) {
      failedAssets.forEach((asset: any) => {
        dispatch(invalidateAssetUpload(asset.id));
      });    
    }

    if (_.size(failedLinks) > 0) {
      failedLinks.forEach((link: any) => {
        dispatch(invalidateLinkAsset(link.id));
      });
    }
  }

  const handleFinish = () => {
    const result = onFinish(state.entities);
    if (result === false) {
      return;
    }

    if (source) {
      state.entities.forEach(item => {
        const type = FileService.getFileType(item.metadata);
        let sourceType: 'file' | 'link' | 'cleverstory' | 'existing_assets' = 'existing_assets';

        if (type === 'INTERACTIVE') {
          const page = _.find(newPagesCreated, (a) => { return _.get(a, 'item.id') === item.id; });
          if (page) {
            sourceType = "cleverstory";
          }
        } else if(type === 'LINK'){
          const link = _.find(newLinksCreated, (a) => { return _.get(a, 'item.id') === item.id; });
          if (link) {
            sourceType = "link";
          }
        } else {
          const asset = _.find(newAssetsCreated, (a) => { return _.get(a, 'item.id') === item.id; });
          if (asset) {
            sourceType = "file";
          }
        }

        trackAssetAdded({
          ...source,
          source: sourceType,
          asset: item,
        });
      });
    }

    const uploads = state.entities || [];
    if(_.size(uploads) > 0) {
      _.map(uploads, (item) => {
        if(state.option != "hub-assets"){
          dispatch(toggleAssetStatusInContentSimilarityQueue({asset: item, initialise: true}));
        }
      })
    }

    invalidateNewEntities();
    onClose();
  }

  useEffect(() => {
    onSelectionChange?.(state.entities);
  }, [state.entities]);

  return (
    <Slider.Dialog show={show} width={hasEntities && !onBack ? "720px" : "380px"} className={cx(styles.dialog,className)} onClose={handleClose}>
      <Slider.Body className={styles.body}>
        <AddAssetsPanel state={state}
          selectableEntityTypes={selectableEntityTypes}
          allowInternalEntities={allowInternalEntities}
          source={source}
          allowBackgroundUpload={allowBackgroundUpload}
          entityAction={entityAction}
          selectionFilter={selectionFilter}
          onChange={handleStateChange}
          onClose={handleClose}
          onBack={onBack}
        />
      </Slider.Body>
      {!onBack && <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text="Confirm"
          onClick={handleFinish}
          disabled={!canFinish}
        />
        <Button
          variant="secondary"
          text="Cancel"
          onClick={handleClose}
        />
      </Slider.Actions>}
    </Slider.Dialog>
  );
};

interface AddAssetsPanelProps {
  state: AssetState;
  selectableEntityTypes: string[];
  source?: SourceProps;
  allowBackgroundUpload?: boolean;
  allowInternalEntities?: boolean;

  // eslint-disable-next-line no-unused-vars
  selectionFilter?: (entity: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;

  // eslint-disable-next-line no-unused-vars
  onChange: (state: AssetState) => void;
  onClose?: () => void;
  onBack?: () => void;
}

const AddAssetsPanel: React.FC<AddAssetsPanelProps> = ({ 
  state, selectableEntityTypes, source, allowBackgroundUpload, allowInternalEntities, onChange, onClose, selectionFilter, entityAction, onBack}) => {
  return (  
    <div className={styles.assetPanel}>
      <AddAssets width={375} state={state}
        selectableEntityTypes={selectableEntityTypes}
        source={source}
        allowInternalEntities={allowInternalEntities}
        selectionFilter={selectionFilter}
        entityAction={entityAction}
        onStateChange={onChange}
        onClose={onClose}
        onBack={onBack}
      />
      {!onBack && <AssetSelection style={{}} state={state} onStateChange={onChange} />}
    </div>
  );
}

interface AddAssetsProps {
  width: number;
  selectableEntityTypes: string[];
  state: AssetState;
  source?: SourceProps;
  allowInternalEntities?: boolean;

  // eslint-disable-next-line no-unused-vars
  selectionFilter?: (entity: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  entityAction?: (entity: any) => any;

  onDropOver?: () => void;
  onDropOut?: () => void;
  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;
  onClose?: () => void;
  className? : string;
  onBack? : () => void
}

const AddAssets: React.FC<AddAssetsProps> = ({ className, width, selectableEntityTypes, state, source, allowInternalEntities, onStateChange, onClose, onDropOver, onDropOut, selectionFilter, entityAction, onBack }) => {
  const dispatch = useDispatch();

  const dropZoneRef = React.useRef<DropzoneRef>(null);

  const { trackAssetSelected } = useAssetAdoption();

  const showOptions = state.option === null;
  const showHub = state.option === 'hub-assets';
  const showCleverStory = state.option === 'cleverstory-assets';

  const [dragging, setDragging] = React.useState(false);

  const optionsTransition = useTransition(showOptions, {
    initial: { opacity: 1, transform: 'translateX(0%)' },
    from: { opacity: 0, transform: 'translateX(-100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(-100%)' },
  });

  const hubTransition = useTransition(showHub, {
    initial: { opacity: 0, transform: 'translateX(100%)' },
    from: { opacity: 0, transform: 'translateX(100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(100%)' },
  });

  const cleverStoryTransition = useTransition(showCleverStory, {
    initial: { opacity: 0, transform: 'translateX(100%)' },
    from: { opacity: 0, transform: 'translateX(100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(100%)' },
  });

  const uploads = useSelector(reduxState => {
    let assets = state.entities || [];

    const uploadState = _.get(reduxState, 'asset.upload');
    const items = _.keys(uploadState).map(key => {
      const upload = _.get(uploadState, `[${key}]`);
      return upload;
    });

    const createdAssets = items.filter(asset => {
      const created = _.get(asset, 'created');
      return created;
    });

    const createdLinks = _.keys((_.get(reduxState, 'asset.linkAsset') || {})).map(key => {
      const link = _.get(reduxState, `asset.linkAsset[${key}]`);
      return link;
    }).filter(item => {
      return _.get(item, 'created');
    });

    const createdPages = _.keys((_.get(reduxState, 'cleverStory.create') || {})).map(key => {
      const page = _.get(reduxState, `cleverStory.create[${key}]`);
      return page;
    }).filter(item => {
      return _.get(item, 'created');
    });

    const uploadingAssets = items.filter(asset => {
      const created = _.get(asset, 'created');
      return !created;
    }).map((item: any) => {
      return {
        id: item.id,
        entityType: 'asset-upload',
        name: item.item.name,
        progress: _.get(item, 'item.progress', 0),
        error: _.get(item, "error", null)
      };
    });

    assets = assets.map(asset => {
      const createdAsset = createdAssets.find(item => (item as any).id === asset.id);

      if (createdAsset) {
        const item = _.get(createdAsset, 'item') as Asset;
        return {
          ...item,
          entityType: item?.entityType || 'asset',
        };
      }

      const createdLink = createdLinks.find(item => (item as any).id === asset.id);

      if (createdLink) {
        const item = _.get(createdLink, 'item');
        return item;
      }

      if(createdPages){
        const createdPage = createdPages.find(item => item != null && (item as any).id === asset?.id);
        if (createdPage) {
          const item = _.get(createdPage, 'item');
          return item;
        }
      }

      if (createdLink) {
        const item = _.get(createdLink, 'item');
        return item;
      }

      const existingAsset = createdAssets.find(item => _.get(item, 'item.id') === asset.id);
      if (existingAsset) {
        const item = _.get(existingAsset, 'item') as Asset;
        return {
          ...item,
          entityType: item?.entityType || 'asset',
        };
      }

      const existingLink = createdLinks.find(item => _.get(item, 'item.id') === asset.id);
      if (existingLink) {
        const item = _.get(existingLink, 'item');
        return item;
      }

      const existingPage = createdPages.find(item => item != null && _.get(item, 'item.id') === asset?.id);
      if (existingPage) {
        const item = _.get(existingPage, 'item');
        return item;
      }

      const uploadingAsset = uploadingAssets.find(item => item.id === asset.id);
      if (uploadingAsset) {
        return uploadingAsset
      }

      if (asset?.entityType === 'collection') {
        const collection = _.get(reduxState, `collections.collection[${asset.id}].item`);
        if (collection) {
          return collection;
        }
      }

      return asset;
    });

    const newUploads = items.filter((asset: any) => {
      return !_.includes(assets, { id: asset.id });
    }).filter(item => {
      return !_.get(item, 'created');
    });

    const links = _.keys((_.get(reduxState, 'asset.linkAsset') || {})).map(key => {
      const link = _.get(reduxState, `asset.linkAsset[${key}]`);
      return link;
    }).filter(item => {
      return !_.get(item, 'created');
    }).map((item: any) => {
      return {
        id: item.id,
        entityType: 'link',
        url: item.url,
      }
    });

    const allPages = _.keys(_.get(reduxState, 'cleverStory.create', {})).map(key => {
      return _.get(reduxState, `cleverStory.create[${key}]`);
    });

    let validPages = allPages.filter((item: any) => !item?.error);
    validPages = validPages.filter(item => !_.get(item, "created"));
    
    const pages = validPages.map((item: any) => ({
      id: item.id,
      entityType: 'cleverstory',
      item: item.page
    }));

    if (_.size(newUploads) > 0) {
      assets = [...assets, ...newUploads];
    }

    if (_.size(links) > 0) {
      assets = [...assets, ...links];
    }

    if (_.size(pages) > 0) {
      assets = [...assets, ...pages];
    }

    if (!assets) {
      assets = [];
    } else {
      assets = assets.filter(item => !_.isEmpty(item));
      assets = _.uniqBy(assets, 'id');
    }

    return assets;
  }, _.isEqual);

  const uploadedAssets = useSelector(reduxState => {
    const uploadState = _.get(reduxState, 'asset.upload');
    const items = _.keys(uploadState).map(key => {
      const upload = _.get(uploadState, `[${key}]`);
      return upload;
    });
    const createdAssets = items.filter(asset => {
      const created = _.get(asset, 'created');
      return created;
    });

    const createdLinks = _.keys((_.get(reduxState, 'asset.linkAsset') || {})).map(key => {
      const link = _.get(reduxState, `asset.linkAsset[${key}]`);
      return link;
    }).filter(item => {
      return _.get(item, 'created');
    });

    const createdPages = _.keys((_.get(reduxState, 'cleverStory.create') || {})).map(key => {
      const page = _.get(reduxState, `cleverStory.create[${key}]`);
      return page;
    }).filter(item => {
      return _.get(item, 'created');
    });

    return [...createdAssets, ...createdLinks, ...createdPages];
  }, _.isEqual);

  useEffect(() => {
    if (_.size(uploads) > 0 && source) {
      uploads.filter(item => {
        return _.get(item, "entityType") === 'asset' && _.find(state.entities, { id: item.id }) == null;
      }).forEach(item => {
        let sourceType: 'file' | 'link' | 'cleverstory' | 'existing_assets' = 'file';

        const type = FileService.getFileType(item.metadata);
        if (type === 'INTERACTIVE') {
          sourceType = 'cleverstory';
        } else if (type === 'LINK') {
          sourceType = 'link';
        }

        trackAssetSelected({
          ...source,
          asset: item,
          source: sourceType
        });
      });
    }

    onStateChange({
      ...state,
      entities: uploads
    });
    

    //  filter out collection and replace with uploaded assets
    const filteredAssets = (uploads || []).filter(entity => {
      return (entity?.entityType === "asset-upload") || 
        (entity?.entityType === 'asset') ||
        (entity?.entityType === 'link') || // link assets
        (entity?.item?.entityType === 'asset-upload') || 
        (entity?.item?.entityType === 'asset') ;
      }).map(entity => {
        return entity
      });

    const isOpenedFromCollection = _.get(source, 'isOpenedFromCollection', false);
    if (isOpenedFromCollection && _.size(uploadedAssets) > 0 && _.size(filteredAssets) > 0){
      onStateChange({
        ...state,
        entities: filteredAssets
      });
    }

  }, [uploads]);

  const onDrop = useCallback((acceptedFiles) => {
    handleDropLeave();

    // Do something with the files
    acceptedFiles.forEach((file: any) => {
      uploadFile(file);
    });
  }, []);

  const handleDropEnter = () => {
    setDragging(true);

    if (onDropOver) {
      onDropOver();
    }
  }

  const handleDropLeave = () => {
    setDragging(false);

    if (onDropOut) {
      onDropOut();
    }
  }

  const handleDropOver = () => {
    setDragging(true);

    if (onDropOver) {
      onDropOver();
    }
  }

  const handleSelection = (entity: any) => {
    const canSelectEntity = _.includes(selectableEntityTypes, entity.entityType) &&
      (selectionFilter ? selectionFilter(entity) : true);

    if (canSelectEntity) {

      if (source && entity.entityType === 'asset') {
        trackAssetSelected({
          ...source,
          source: 'existing_assets',
          asset: entity as Asset
        });
      }

      const existing = state.entities || [];
      let updatedEntities: any[] = [];

      if (entity.entityType === 'collection') {
        // If a collection is clicked, REPLACE the entire selection
        updatedEntities = [entity];
      } else {
        // If an asset is clicked, clear any collection and APPEND
        const cleanEntities = existing.filter((item) => item.entityType !== 'collection');
        updatedEntities = _.uniqBy(
        [...cleanEntities.filter((item) => item.id !== entity.id), entity], 
        'id');
      }

      onStateChange({
        ...state,
        entities: updatedEntities,
      });
    }
  };

  const uploadFile = (file) => {
    file.id = nanoid();
    const fileName = _.get(file, "name", "Untitled");
    let fileType = FileService.getFileExtension(fileName);
    if (file.type == "" || file.type == null || file.type == undefined) {
      fileType = "application/octet-stream";
      file = new File([file], fileName, { type: fileType })
      file.id = nanoid();
    }

    const s3Uploader = new S3Upload({
      signingUrl: "/file_upload/initiate",
      signingUrlMethod: "POST",
      signingUrlHeaders: {
        Authorization: `Bearer ${AuthService.getAccessToken()}`,
      },
      server: apiUrl,
      scrubFilename: (filename) => {
        const name = filename.replace(/[^\w\d_\-.\s]+/ig, "");
        return encodeURIComponent(name);
      },
      signingUrlQueryParams: { originalName: encodeURIComponent(fileName)},
      contentDisposition: "auto",
      uploadRequestHeaders: {},
      onFinishS3Put: (res) => handleFileUploadFinish(res, file, fileName),
      onProgress: handleFileUploadProgress,
      onError: handleFileUploadError,
      files: [file],
    });

    FileUploadService.trackUpload(s3Uploader);

    dispatch(uploadAsset({
      file: {
        id: file.id,
        name: fileName,
        type: fileType,
        size: file.size,
      } as AssetFile
    }));
  }

  const handleFileUploadError = (file, error) => {
    dispatch(uploadAssetFailed({
      id: file.id,
      error: error
    }));
  }

  const handleFileUploadProgress = (progress, status, file) => {
    dispatch(uploadAssetProgress({
      id: file.id,
      progress: progress
    }));
  }

  const handleFileUploadFinish = (data, file, fileName) => {
    FileUploadService.markFinish(file.id);
    dispatch(uploadingAssetSucceeded({
      id: file.id
    }));

    dispatch(createAssetFromFile({
      id: file.id,
      name: fileName,
      signedUrl: data.signedUrl,
    }));
  }

  const handleBrowseClicked = () => {
    if (dropZoneRef.current) {
      dropZoneRef.current.open();
    }
  }

  return (
    <Dropzone ref={dropZoneRef} noClick={true}
      onDrop={onDrop}
      onDragEnter={handleDropEnter}
      onDragLeave={handleDropLeave}
      onDragOver={handleDropOver}
    >
      {({ getRootProps, getInputProps }) => (
        <div className={styles.addAsset} style={{ width: width }}  {...getRootProps()}>
          <input {...getInputProps()} type="file" />
          <Header state={state} onStateChange={onStateChange} onClose={onClose} className={className} onBack={onBack}/>
          <div className={cx(styles.stacks, styles.content)}>
            {optionsTransition(
              (style, item) =>
                item && (
                  <AddAssetsOptions
                    style={style}
                    state={state}
                    source={source}
                    onStateChange={onStateChange}
                    onBrowse={handleBrowseClicked}
                  />
                ),
            )}

            {hubTransition(
              (style, item) =>
                item && (
                  <AddAssetsFromHub
                    style={style}
                    className ={className}
                    width={width}
                    allowInternalEntities={allowInternalEntities}
                    selectableEntityTypes={selectableEntityTypes}
                    onSelection={handleSelection}
                    entityAction={entityAction}
                    source={source}
                  />
                ),
            )}

            {cleverStoryTransition(
              (style, item) =>
                item && (
                  <AddAssetFromCleverStory
                    style={style}
                    onSelection={handleSelection}
                    selectedAssets={state.entities}
                  />
                ),
            )}

            {dragging && <FileDropIndicator /> }
          </div>
        </div>
      )}
    </Dropzone>
  );
};

interface HeaderProps {
  state: AssetState;
  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;
  onClose?: () => void;
  className?: string;
  onBack?: ()=> void;
}

const Header: React.FC<HeaderProps> = ({ state, onStateChange, onClose,className, onBack }) => {
  const showPrimary = state.option === null;
  const showSecondary =
    state.option === "hub-assets" || state.option === "cleverstory-assets";

  const getTextForHeader = (textType) => {
    if (textType === "title") {
      switch (state.option) {
        case "hub-assets":
          return <h5 className={styles.title}>streams & collections</h5>;
        case "cleverstory-assets":
          return (
            <img
              height={28}
              width={110}
              src="/images/assets/add/cleverstory.png"
              alt="cleverstory"
            />
          );
        default:
          break;
      }
    } else if (textType === "subTitle") {
      switch (state.option) {
        case "hub-assets":
          return "Some of your best content is on the hub. Go get it!";
        case "cleverstory-assets":
          return "Add assets from Cleverstory storyboards";
        default:
          break;
      }
    }
  };

  const primaryTransition = useTransition(showPrimary, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const secondaryTransition = useTransition(showSecondary, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { tension: 120, friction: 14 },
  });

  const handleBackClicked = () => {
    onStateChange({ ...state, option: null });
  };

  return (
    <div className={cx(styles.header, className)}> 
          {primaryTransition(
        (style, item) =>
          item && (
            <animated.div className={styles.primary} style={style}>
              <div className={styles.row}>
                <div className={styles.left}>
                 {onBack && <i className="paperflite-left-arrow" onClick={onBack} />}
                  <h5 className={styles.title}>adding assets</h5>
                </div>
                {onClose && (
                  <button className={styles.close} onClick={onClose}>
                    <i className="paperflite-close"></i>
                  </button>
                )}
              </div>
              <p className={styles.message}>&nbsp;</p>
            </animated.div>
          ),
      )}

      {secondaryTransition(
        (style, item) =>
          item && (
            <animated.div className={styles.secondary} style={style}>
              <div className={styles.row}>
                <Spring
                  to={{
                    opacity: 1,
                  }}
                  from={{
                    opacity: 0,
                  }}
                  config={{
                    duration: 300,
                  }}
                >
                  {(style) => (
                    <animated.button
                      style={style}
                      className={styles.back}
                      onClick={handleBackClicked}
                    >
                      <i className="paperflite-left-arrow"></i>
                    </animated.button>
                  )}
                </Spring>
                {getTextForHeader("title")}
              </div>
              <p className={styles.message}>{getTextForHeader("subTitle")}</p>
            </animated.div>
          ),
      )}
    </div>
  );
};

interface AddAssetsOptionProps {
  style: any;
  state: AssetState;
  source?: SourceProps;

  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;

  onBrowse: () => void;
}

const AddAssetsOptions: React.FC<AddAssetsOptionProps> = ({
  style,
  state,
  source,
  onStateChange,
  onBrowse
}) => {

  const { trackAssetSourceSelected } = useAssetAdoption();

  const cleverstoryIntegrationEnabled = useSelector((state: any) => {
    const integrated = _.get(state, "accounts.item.cleverstoryIntegration");
    const workspaces = _.get(state, "cleverStory.workspaces");

    return integrated && _.size(workspaces) > 0;
  });

  const handleExistingAssetsClicked = () => {
    onStateChange({
      ...state,
      option: "hub-assets",
    });

    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'existing_assets'
      });
    }
  };

  const handleAddFromCleverStoryClicked = () => {
    onStateChange({
      ...state,
      option: "cleverstory-assets",
    });

    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'cleverstory'
      });
    }
  };

  const handleBrowse = () => {
    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'file'
      });
    }

    if (onBrowse) {
      onBrowse();
    }
  }

  const handleLinkCreated = () => {
    if (source) {
      trackAssetSourceSelected({
        ...source,
        source: 'link'
      });
    }
  }

  return (
    <animated.div className={styles.options} style={style}>
      <div
        className={cx(styles.option, styles.addFromHub)}
        onClick={handleExistingAssetsClicked}
      >
        <img
          src="/images/assets/add/add_from_hub_collections.svg"
          alt="Streams & collections"
        />
        <div className={styles.details}>
          <h6 className={styles.title}>Streams & collections</h6>
          <span className={styles.description}>
            Choose your favourite assets from within Paperflite. No judgements here
          </span>
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={styles.action}
              icon="paperflite-add"
              text="Add"
            />
          </div>
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.option}>
        <img src="/images/assets/add/add_from_file.svg" alt="Drag & drop" />
        <div className={styles.details}>
          <h6 className={styles.title}>Personal drives</h6>
          <span className={styles.description}>
            Got something better on your device? Drag and drop it here.
          </span>
          <div className={styles.actions}>
            <Button
              variant="tertiary"
              className={styles.action}
              icon="paperflite-add"
              text="Browse"
              onClick={handleBrowse}
            />
          </div>
        </div>
      </div>

      {cleverstoryIntegrationEnabled && (
        <>
          <div className={styles.divider} />
          <div
            className={cx(styles.option, styles.addFromCleverStory)}
            onClick={handleAddFromCleverStoryClicked}
          >
            <img
              src="/images/assets/add/add_from_cleverstory.svg"
              alt="add from cleverstory"
            />
            <div className={styles.details}>
              <img
                height={24}
                width={95}
                src="/images/assets/add/cleverstory.png"
                alt="cleverstory"
              />
              <span className={styles.description}>
                Select assets from Cleverstory. Because everyone loves a good story.
              </span>
              <div className={styles.actions}>
                <Button
                  variant="tertiary"
                  className={styles.action}
                  icon="paperflite-add"
                  text="Add"
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div className={styles.divider} />
      <div className={styles.option}>
        <img src="/images/assets/add/add_from_url.svg" alt="Add content URL" />
        <div className={styles.details}>
          <h6 className={styles.title}>Add content URL</h6>
          <AddUrlAsset onCreate={handleLinkCreated} />
        </div>
      </div>
    </animated.div>
  );
};

interface AssetSelectionProps {
  style: any;
  state: AssetState;
  // eslint-disable-next-line no-unused-vars
  onStateChange: (state: AssetState) => void;
}

const AssetSelection: React.FC<AssetSelectionProps> = ({
  state,
  onStateChange
}) => {
  const dispatch = useDispatch();

  const assets = state.entities || [];

  const handleRemove = (asset: Asset | AssetUpload) => {
    if (asset.entityType === "asset-upload") {
      const uploader = FileUploadService.getUploader(asset.id);

      if (uploader) {
        console.log(`Aborting upload ${asset.id}`);
        uploader.abortUpload();
      }
      dispatch(invalidateAssetUpload(asset.id));
    }

    if (asset.entityType === "link") {
      dispatch(invalidateLinkAsset(asset.id));
    }

    const assets = state.entities.filter((entity) => entity.id !== asset.id);
    onStateChange({
      ...state,
      entities: assets
    });
  }

  return (
    <animated.div className={styles.entitySelectionContainer}>
      <div className={styles.entityHeader}>
        <h6 className={styles.title}>{_.size(assets)} Assets</h6>
      </div>
      <div className={styles.entities}>
        {assets.map(asset => {
          const entityType = _.get(asset, 'entityType');

          if (entityType === 'asset-upload') {
            return (
              <div className={styles.asset} key={asset.id}>
                <AssetUploadCard asset={asset as AssetUpload} onRemove={handleRemove} />
              </div>
            );
          } else if (entityType === 'asset') {
            return (
              <div className={styles.asset} key={asset.id}>
                <AssetCard asset={asset as Asset} editable={true} onRemove={handleRemove} />
              </div>
            );
          } else if (entityType === 'link') {
            return (
              <div className={styles.asset} key={asset.id}>
                <LinkAssetCard url={asset.url} onRemove={handleRemove} linkAsset={asset} />
              </div>
            );
          } else if (entityType === 'cleverstory') {
            return (
              <div className={styles.asset} key={asset.id}>
                <CleverstoryAssetCard page={asset.item} />
              </div>
            );
          } else if(entityType === 'podcast') {
            return (
              <div className={styles.asset} key={asset.id}>
                <PodcastCard asset={asset} onRemove={handleRemove} />
              </div>
            )
          } else if(entityType === 'simulation') {
            return (
              <div className={styles.asset} key={asset.id}>
                <SimulationCard asset={asset} onRemove={handleRemove} />
              </div>
            )
          }
        })}
      </div>
    </animated.div>
  );
};

const FileDropIndicator: React.FC = () => {
  return (
    <div className={styles.dragDrop}>
      <div className={styles.dragDropContent}>
        <img src="/images/assets/add/drag_drop.svg" alt="" />
        <p className={styles.message}>Drop files here to upload</p>
      </div>
    </div>
  );
}

export { AddAssetsDialog, AddAssetsPanel, AddAssets, FileDropIndicator, AssetSelection };
